import { lazy, Suspense, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { AuthLayout } from '@/components/shell/AuthLayout';
import { Alert } from '@/components/ui/feedback';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Inputs';
import { IconButton } from '@/components/ui/Button';
import { isApiError } from '@/lib/api/client';
import { env, IS_DEMO } from '@/lib/env';
import { useFocusOnMount } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '../AuthProvider';

// One-click demo sign-in (loaded only in the demo build; the `@/demo/*` alias is an inert stub in live builds).
const DemoAccounts = IS_DEMO ? lazy(() => import('@/demo/DemoAccounts')) : null;

const DEMO_PASSWORD = 'Lumina@2026';
const DEMO_ACCOUNTS: Record<'staff' | 'patron', { login: string; label: string }[]> = {
  staff: [
    { login: 'admin', label: 'Super admin' },
    { login: 'librarian', label: 'Librarian' },
    { login: 'clerk', label: 'Circulation clerk' },
    { login: 'cataloger', label: 'Cataloger' },
    { login: 'guard', label: 'Door guard' },
  ],
  patron: [
    { login: 'aarav.sharma', label: 'Student' },
    { login: 'kavita.deshmukh', label: 'Faculty' },
    { login: 'ramesh.kulkarni', label: 'Senior member' },
  ],
};

interface LoginFormProps {
  audience: 'staff' | 'patron';
  /** Where to go after sign-in when there is no `state.from`. */
  home: string;
  forgotTo: string;
}

function destination(from: unknown, home: string): string {
  // Only follow same-origin absolute paths so a crafted state can never bounce a user to another site.
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : home;
}

export function LoginForm({ audience, home, forgotTo }: LoginFormProps) {
  const { login, logout, status, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  const loginRef = useFocusOnMount<HTMLInputElement>();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ login?: string; password?: string }>({});

  if (status === 'authenticated' && user && (audience === 'patron' || user.isStaff)) {
    return <Navigate to={destination(from, home)} replace />;
  }

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const errors: typeof fieldErrors = {};
    if (!identifier.trim()) {
      errors.login = t('error.fieldRequired');
    }
    if (!password) {
      errors.password = t('error.fieldRequired');
    }
    setFieldErrors(errors);
    if (errors.login || errors.password) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const signedIn = await login({ login: identifier.trim(), password });
      if (audience === 'staff' && !signedIn.isStaff) {
        await logout();
        setError('This account does not have access to the staff console. Use the patron sign-in instead.');
        return;
      }
      navigate(destination(from, home), { replace: true });
    } catch (e) {
      setError(isApiError(e) && (e.status === 401 || e.code === 'INVALID_CREDENTIALS') ? t('auth.invalidCredentials') : isApiError(e) ? e.message : t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  const staff = audience === 'staff';
  return (
    <AuthLayout
      audience={audience}
      title={staff ? t('auth.staffSignInTitle') : t('auth.signInTitle')}
      subtitle={staff ? t('auth.staffNotice') : undefined}
      footer={
        staff ? (
          <Link to="/login" className="font-medium text-sapphire-700 hover:underline dark:text-sapphire-300">
            {t('nav.signIn')} ({t('nav.account')})
          </Link>
        ) : (
          <>
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="font-medium text-sapphire-700 hover:underline dark:text-sapphire-300">
              {t('nav.register')}
            </Link>
          </>
        )
      }
    >
      <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-5">
        {error && (
          <Alert tone="danger" dense>
            {error}
          </Alert>
        )}
        <Field label={t('auth.login')} error={fieldErrors.login} required>
          <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" autoCapitalize="off" spellCheck={false} ref={loginRef} size="lg" />
        </Field>
        <Field
          label={t('auth.password')}
          error={fieldErrors.password}
          required
          labelAside={
            <Link to={forgotTo} className="text-xs font-medium text-sapphire-700 hover:underline dark:text-sapphire-300">
              {t('auth.forgot')}
            </Link>
          }
        >
          <Input
            type={show ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            size="lg"
            rightSlot={<IconButton label={show ? 'Hide password' : 'Show password'} size="sm" icon={show ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />} onClick={() => setShow((s) => !s)} />}
          />
        </Field>
        <Button type="submit" size="lg" fullWidth loading={busy} leftIcon={<LogIn className="h-4 w-4" aria-hidden="true" />}>
          {busy ? t('auth.signingIn') : t('nav.signIn')}
        </Button>
      </form>
      {DemoAccounts && (
        <Suspense fallback={null}>
          <DemoAccounts audience={audience} home={home} />
        </Suspense>
      )}
      {env.demoMode && !IS_DEMO && (
        <div className="mt-6 rounded-lg border border-dashed border-sapphire-300 p-4 dark:border-sapphire-700" data-testid="demo-accounts">
          <p className="text-sm font-medium">Demo accounts</p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">Sample data. Pick one to fill the form, then sign in. Password for all: {DEMO_PASSWORD}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {DEMO_ACCOUNTS[audience].map((a) => (
              <button
                key={a.login}
                type="button"
                className="rounded-full border border-sapphire-200 px-3 py-1 text-xs font-medium hover:bg-sapphire-50 dark:border-sapphire-700 dark:hover:bg-sapphire-900"
                onClick={() => {
                  setIdentifier(a.login);
                  setPassword(DEMO_PASSWORD);
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
